import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Divider,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { getBusinessData } from '../../common/utils';

interface ReceiptItem {
  orderID?: string;
  productName?: string;
  description?: string;
  quantity?: number | string;
  unitPrice?: number;
  amount?: number;
  status?: string;
  price?: number; // Some APIs use 'price' instead of 'unitPrice'
  total?: number; // Some APIs use 'total' instead of 'amount'
  product_name?: string; // Some APIs use 'product_name' instead of 'productName'
}

interface ReceiptData {
  customer?: {
    name?: string;
    phone?: string;
    email?: string;
  };
  order?: {
    id?: string;
    date?: string;
    status?: string;
    paymentStatus?: string;
    paymentMethod?: string;
  };
  items?: ReceiptItem[];
  summary?: {
    totalAmount?: number;
    paidAmount?: number;
    balanceDue?: number;
  };
}

const ReceiptModal = ({ open, onClose, receiptData }: { 
  open: boolean; 
  onClose: () => void;
  receiptData?: ReceiptData 
}) => {
  const handlePrint = () => {
    const content = document.getElementById('receipt-content');
    if (!content) return;

    const printWindow = window.open('', '', 'width=320,height=600');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Receipt</title>
          <style>
            body { 
              font-family: 'Courier New', monospace; 
              font-size: 12px; 
              margin: 0; 
              padding: 5px; 
              width: 80mm;
            }
            .receipt-header { text-align: center; margin-bottom: 8px; }
            .receipt-title { font-weight: bold; font-size: 14px; }
            .receipt-subtitle { font-size: 11px; }
            .divider { border-top: 1px dashed #000; margin: 5px 0; }
            .section { margin: 5px 0; }
            .section-title { font-weight: bold; margin-bottom: 3px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { padding: 2px 1px; text-align: left; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .total-row { font-weight: bold; border-top: 1px dashed #000; }
            .footer { text-align: center; margin-top: 10px; font-style: italic; }
            .product-name { font-weight: bold; }
            .product-desc { font-size: 10px; color: #555; }
            .logo { max-width: 50px; max-height: 50px; display: block; margin: 0 auto 5px; }
            .item-table { width: 100%; margin-bottom: 5px; }
            .item-row { border-bottom: 1px dashed #ccc; }
            .amount { font-weight: bold; }
          </style>
        </head>
        <body>
          ${content.innerHTML}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const getSafe = <T,>(obj: any, path: string, defaultValue: T): T => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj) || defaultValue;
  };

  // Helper function to safely parse numbers with better handling
  const safeNumber = (value: any, defaultValue: number = 0): number => {
    if (value === null || value === undefined) return defaultValue;
    
    const num = Number(value);
    // Check if it's a valid number and not NaN or Infinity
    if (isNaN(num) || !isFinite(num)) return defaultValue;
    
    return num;
  };

  // Helper function to format currency
  const formatCurrency = (value: any): string => {
    const num = safeNumber(value);
    return `₦${num.toLocaleString()}`;
  };

  // Enhanced helper function to calculate amount with multiple field options
  const calculateAmount = (item: ReceiptItem): number => {
    console.log('Calculating amount for item:', item);
    
    // Try all possible amount fields
    const possibleAmounts = [
      item.amount,      // amount field
      item.total,       // total field (some APIs)
      safeNumber(item.quantity, 1) * safeNumber(item.unitPrice || item.price, 0) // calculated
    ];
    
    // Find the first valid amount
    for (const amount of possibleAmounts) {
      const numAmount = safeNumber(amount);
      if (numAmount > 0) {
        console.log(`Found amount ${numAmount} for item:`, item.productName || item.product_name);
        return numAmount;
      }
    }
    
    // If no valid amount found, calculate from quantity and price
    const quantity = safeNumber(item.quantity, 1);
    const unitPrice = safeNumber(item.unitPrice || item.price, 0);
    const calculated = quantity * unitPrice;
    
    console.log(`Calculated amount ${calculated} for item:`, item.productName || item.product_name);
    return calculated;
  };

  // Enhanced helper function to calculate total from items
  const calculateTotalAmount = (items: ReceiptItem[] = []): number => {
    console.log('Calculating total from items:', items);
    
    if (!items || items.length === 0) {
      console.log('No items to calculate total from');
      return 0;
    }
    
    const total = items.reduce((sum, item, index) => {
      const itemAmount = calculateAmount(item);
      console.log(`Item ${index + 1} "${item.productName || item.product_name || 'Unknown'}" amount:`, itemAmount);
      return sum + itemAmount;
    }, 0);
    
    console.log('Total calculated:', total);
    return total;
  };

  if (!receiptData) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Receipt Error</DialogTitle>
        <DialogContent>
          <Alert severity="error">No receipt data available</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // Debug: Log the entire receipt data
  console.log('Receipt Data:', receiptData);
  console.log('Items array:', receiptData.items);

  // Safely get values with calculations
  const items = receiptData.items || [];
  console.log('Items count:', items.length);
  
  // Calculate total
  const calculatedTotal = calculateTotalAmount(items);
  const summaryTotal = safeNumber(receiptData.summary?.totalAmount);
  
  // Use summary total if valid, otherwise use calculated total
  const totalAmount = summaryTotal > 0 ? summaryTotal : calculatedTotal;
  
  const paidAmount = safeNumber(receiptData.summary?.paidAmount, 0);
  const balanceDue = safeNumber(receiptData.summary?.balanceDue, totalAmount - paidAmount);

  console.log('Final totals:', {
    calculatedTotal,
    summaryTotal,
    totalAmount,
    paidAmount,
    balanceDue
  });

  // Get product name from multiple possible fields
  const getProductName = (item: ReceiptItem): string => {
    return item.productName || item.product_name || 'Unknown Product';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ py: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Payment Receipt</Typography>
          <Button onClick={onClose} size="small">
            Close
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box id="receipt-content" sx={{ p: 1 }}>
          {/* Header */}
          <Box className="receipt-header">
            {/* Logo */}
            {getBusinessData()?.logoUrl && (
              <img 
                src={getBusinessData().logoUrl} 
                alt="Logo" 
                className="logo"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
            <Typography className="receipt-title">{getBusinessData()?.name || 'Business Name'}</Typography>
            <Typography className="receipt-subtitle">PAYMENT RECEIPT</Typography>
            <Typography variant="caption">
              {getBusinessData()?.address || 'Business Address'}
            </Typography>
            <Typography variant="caption" display="block">
              Tel: {getBusinessData()?.phone || 'N/A'} | Email: {getBusinessData()?.email || 'N/A'}
            </Typography>
          </Box>

          <Divider className="divider" />

          {/* Order & Customer Info */}
          <Box className="section">
            <Typography className="section-title">ORDER INFO</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 'none', p: 0.5, width: '40%' }}>Order ID:</TableCell>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>{getSafe(receiptData, 'order.id', 'N/A')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>Date:</TableCell>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>{getSafe(receiptData, 'order.date', new Date().toLocaleDateString())}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>Customer:</TableCell>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>{getSafe(receiptData, 'customer.name', 'N/A')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>Phone:</TableCell>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>{getSafe(receiptData, 'customer.phone', 'N/A')}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Divider className="divider" />

          {/* Payment Info */}
          <Box className="section">
            <Typography className="section-title">PAYMENT DETAILS</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>Status:</TableCell>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>{getSafe(receiptData, 'order.paymentStatus', 'N/A')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>Method:</TableCell>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>{getSafe(receiptData, 'order.paymentMethod', 'N/A')}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>Order Status:</TableCell>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>{getSafe(receiptData, 'order.status', 'N/A')}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Divider className="divider" />

          {/* Order Items */}
          <Box className="section">
            <Typography className="section-title">ORDER ITEMS ({items.length})</Typography>
            {items.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ p: 0.5, fontSize: '10px', width: '50%' }}>Item</TableCell>
                      <TableCell sx={{ p: 0.5, fontSize: '10px', width: '15%' }} align="right">Qty</TableCell>
                      <TableCell sx={{ p: 0.5, fontSize: '10px', width: '15%' }} align="right">Unit Price</TableCell>
                      <TableCell sx={{ p: 0.5, fontSize: '10px', width: '20%' }} align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const itemAmount = calculateAmount(item);
                      const unitPrice = safeNumber(item.unitPrice || item.price);
                      const quantity = safeNumber(item.quantity, 1);
                      
                      console.log(`Rendering item ${index}:`, {
                        name: getProductName(item),
                        quantity,
                        unitPrice,
                        itemAmount
                      });
                      
                      return (
                        <TableRow key={index} className="item-row">
                          <TableCell sx={{ p: 0.5, fontSize: '10px' }}>
                            <div className="product-name">
                              {getProductName(item)}
                           
                            {item.description && (
                             <>
                                :  {item.description}
                              </>
                               
                            )}  </div>
                          </TableCell>
                          <TableCell sx={{ p: 0.5, fontSize: '10px' }} align="right">
                            {quantity}
                          </TableCell>
                          <TableCell sx={{ p: 0.5, fontSize: '10px' }} align="right">
                            {formatCurrency(unitPrice)}
                          </TableCell>
                          <TableCell sx={{ p: 0.5, fontSize: '10px' }} align="right" className="amount">
                            {formatCurrency(itemAmount)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Add subtotal row */}
                    <TableRow>
                      <TableCell colSpan={3} align="right" sx={{ p: 0.5, fontSize: '10px', fontWeight: 'bold' }}>
                        Subtotal:
                      </TableCell>
                      <TableCell align="right" sx={{ p: 0.5, fontSize: '10px', fontWeight: 'bold' }}>
                        {formatCurrency(calculatedTotal)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info" sx={{ py: 0, fontSize: '11px' }}>No items found</Alert>
            )}
          </Box>

          <Divider className="divider" />

          {/* Order Summary */}
          <Box className="section">
            <Typography className="section-title">ORDER SUMMARY</Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell sx={{ border: 'none', p: 0.5, fontWeight: 'bold' }}>Total Amount:</TableCell>
                  <TableCell sx={{ border: 'none', p: 0.5, fontWeight: 'bold' }} align="right">
                    {formatCurrency(totalAmount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ border: 'none', p: 0.5 }}>Amount Paid:</TableCell>
                  <TableCell sx={{ border: 'none', p: 0.5 }} align="right">
                    {formatCurrency(paidAmount)}
                  </TableCell>
                </TableRow>
                <TableRow className="total-row">
                  <TableCell sx={{ border: 'none', p: 0.5, fontWeight: 'bold' }}>Balance Due:</TableCell>
                  <TableCell sx={{ 
                    border: 'none', 
                    p: 0.5, 
                    fontWeight: 'bold',
                    color: balanceDue > 0 ? 'red' : 'green'
                  }} align="right">
                    {formatCurrency(balanceDue)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Divider className="divider" />

          {/* Footer */}
          <Box className="footer">
            <Typography variant="caption" display="block">
              Thank you for your business!
            </Typography>
            <Typography variant="caption" display="block">
              Generated on: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ py: 1 }}>
        <Button 
          variant="contained" 
          startIcon={<PrintIcon />} 
          onClick={handlePrint}
          disabled={!receiptData || items.length === 0}
          size="small"
          fullWidth
        >
          Print Receipt
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReceiptModal;